import { DashboardRadarComponent } from '../dashboard/radar/dashboard-radar.component';
import { BinaryButtonComponent } from '../map/custom-controls/components/binary-button/binary-button.component';
import { MapComponent, MapType } from '../map/map.component';
import { hasTrackingLayer } from '../map/radar/radar-tile.utils';
import { RadarLegendComponent } from './radar.legend.component';

import { AuthService } from '../auth/auth.service';
import { RadarDtg, RadarService } from './radar.service';

import { DatexPipe } from '../shared/datex.pipe';
import { GetSeasonPipe } from './pipes/get-season.pipe';

import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  Injector,
  model,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  createCustomElement,
  NgElement,
  WithProperties,
} from '@angular/elements';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ChangeContext,
  LabelType,
  NgxSliderModule,
  Options,
} from '@angular-slider/ngx-slider';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { setPigeonsRoute } from '../constants/constants';
import { CustomComponentControl } from '../map/custom-controls/custom-component-control';
import { BinaryButton } from '../map/custom-controls/enums/binary-button.enum';
import {
  concentricCircleControlEnabled,
  fcstRunTypes,
  obsRunTypes,
} from './constants/constants';
import { TippyDirective } from '@ngneat/helipopper';
import mapboxgl from 'mapbox-gl';
import moment from 'moment';
import { Moment } from 'moment';
import {
  BehaviorSubject,
  filter,
  map,
  noop,
  Subscription,
  tap,
  timer,
} from 'rxjs';
import { sliderBaseOptions } from 'src/constants';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatSidenavModule,
  ReactiveFormsModule,
  FormsModule,
  MatFormFieldModule,
  MatSelectModule,
  MatSlideToggleModule,
  MatInputModule,
  MatDatepickerModule,
  MatCardModule,
  NgxSliderModule,
  MatDividerModule,
  MatCardModule,
  MatButtonModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaIconComponent,
  MapComponent,
  RadarLegendComponent,
  DashboardRadarComponent,
];
export const DIRECTIVES = [TippyDirective];
export const PIPES = [GetSeasonPipe];

@Component({
  selector: 'app-radar',
  templateUrl: './radar.component.html',
  styleUrls: ['./radar.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_MOMENT_DATE_FORMATS,
    },
  ],
  imports: [
    ...MODULES,
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES,
    DatexPipe,
    MatTooltip,
  ],
})
export class RadarComponent implements OnInit, OnDestroy {
  public radarService = inject(RadarService);
  public auth = inject(AuthService);
  private adapter = inject(DateAdapter<any>);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public translate = inject(TranslateService);
  private injector = inject(Injector);
  private platform = inject(Platform);
  private breakpointObserver = inject(BreakpointObserver);

  private handset = toSignal(
    this.breakpointObserver
      .observe(['(max-width: 900px)'])
      .pipe(map(r => r.matches)),
    {
      initialValue: this.breakpointObserver.isMatched(['(max-width: 950px)']),
    }
  );

  readonly isMobilePlatform = computed(
    () => this.handset() || this.platform.ANDROID || this.platform.IOS
  );

  // Mobile-only UI state: region/zoom and the legend are tucked behind a
  // button bar instead of always being on screen (see radar.component.scss
  // MOBILE RADAR LAYOUT block).
  showMobileRegionPanel = signal(false);
  showMobileLegend = signal(false);

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.onPause();
    }
  }

  @ViewChild(MapComponent) mapComponent: MapComponent;

  regionControl = new UntypedFormControl();
  zoomPointControl = new UntypedFormControl();
  dateControl = new UntypedFormControl();
  regions = signal([]);
  region = signal('de');
  cellTrackingSupported = computed(() => hasTrackingLayer(this.region()));
  centerCitiesAll = [];
  centerCitiesOfRegion = signal([]);
  centerCity = { searchName: 'whole Area' };
  seasonDate = signal<Moment>(undefined);
  allowedDates = signal([]);
  isProductDeactive = signal<boolean>(undefined);
  isArchiveMode = signal(false);

  dtgs = signal<Array<RadarDtg>>(undefined);
  mapType: typeof MapType = MapType;

  sliderOptions = signal<Options>(sliderBaseOptions);
  sliderArchiveOptions = signal<Options>(sliderBaseOptions);
  sliderDate = model<number>(undefined);
  dtgIndex = signal(0);
  animationSubscription: Subscription;
  refreshSubscription: Subscription;
  isPause = signal(true);
  loadingImages = signal(false);
  animationSpeedMapping = {
    1: 2000,
    2: 1500,
    3: 1000,
    4: 500,
    5: 250,
    6: 125,
  };
  animationSpeedOptions: Options = {
    showTicksValues: true,
    stepsArray: [
      { value: 1, legend: 'langsam' },
      { value: 2 },
      { value: 3 },
      { value: 4 },
      { value: 5 },
      { value: 6, legend: 'schnell' },
    ],
  };

  timeRangeOptions: Options = {
    showTicksValues: true,
    stepsArray: [{ value: 60 }, { value: 90 }, { value: 120 }],
    translate: (value: number): string => {
      return value + ' Min';
    },
  };

  settings = signal({
    timerange: 120,
    animationSpeed: 4,
    showCellTracking: true,
    showRadarFcst: true,
    region: null,
  });

  serverError = signal(false);
  sub: Subscription;

  map: mapboxgl.Map;

  concentricCircles$ = new BehaviorSubject<BinaryButton | null>(
    (localStorage.getItem(concentricCircleControlEnabled) as BinaryButton) ??
      BinaryButton.enabled
  );

  radarCirclesPaintEnabled$ = this.concentricCircles$.pipe(
    map(enabled => enabled === BinaryButton.enabled)
  );

  interval = signal(5);

  seasonStarted = computed(() => {
    return (
      !this.isProductDeactive() ||
      (this.isProductDeactive() && this.allowedDates().length > 0)
    );
  });

  private destroyRef = inject(DestroyRef);

  get mapDtg(): Moment {
    return moment.unix(this.sliderDate());
  }

  get isForecast(): boolean {
    if (this.dtgs() && this.dtgs().length > 0) {
      return this.dtgs()[this.dtgIndex()].isFcst;
    }
    return false;
  }

  get isArchive(): boolean {
    if (this.dtgs() && this.dtgs().length > 0) {
      return this.dtgs()[this.dtgIndex()].isArchive;
    }
    return false;
  }

  constructor() {
    this.defineBinaryButtonElement();
  }

  dateFilter = (d: Moment | null): boolean => {
    if (!d) {
      return false;
    }
    return this.allowedDates().indexOf(d.unix()) >= 0;
  };

  private defineBinaryButtonElement() {
    if (isNil(customElements.get('binary-button-element'))) {
      const binaryButtonElement = createCustomElement(BinaryButtonComponent, {
        injector: this.injector,
      });
      customElements.define('binary-button-element', binaryButtonElement);
    }
  }

  ngOnInit(): void {
    this.listenConcentricCirclesButtonSelectionChange$();
    this.readUserSettings();
    this.isProductDeactive.set(!this.auth.isProductActive('radarProducts'));
    this.adapter.setLocale(this.auth.getLanguage());

    const allowedRegions = this.getRegions();
    this.regions.set([...allowedRegions]);

    this.initZoomPoints();

    const initialRegion = this.getAllowedRegionOrFallback(
      this.settings().region
    );

    if (this.settings().region !== initialRegion) {
      this.settings.update(settings => {
        return { ...settings, region: initialRegion };
      });
      this.saveUserSettings();
    }

    this.region.set(initialRegion);
    this.regionControl.setValue(this.region(), { emitEvent: false });
    this.setZoomPointsForRegion();
    this.selectWholeAreaForCurrentRegion();

    this.sub = this.route.params.subscribe(params => {
      if (
        params &&
        params.hasOwnProperty('region') &&
        params.hasOwnProperty('zoom')
      ) {
        const requestedRegion = params['region'];
        const safeRegion = this.getAllowedRegionOrFallback(requestedRegion);

        if (requestedRegion !== safeRegion) {
          this.redirectToFallbackRegion(safeRegion);
          return;
        }

        this.region.set(safeRegion);
        this.regionControl.setValue(this.region(), { emitEvent: false });

        if (this.settings().region !== safeRegion) {
          this.settings.update(settings => {
            return { ...settings, region: safeRegion };
          });
          this.saveUserSettings();
        }

        this.setZoomPointsForRegion();

        const zoomParam = String(params['zoom'] ?? '');
        const [name, lat, lon] = zoomParam.split('_');

        const hasCoords = lat !== undefined && lon !== undefined;
        if (name && hasCoords) {
          this.setCenterCity(name, Number(lon), Number(lat), this.region());
        } else if (name) {
          this.setCenterCity(name, Number.NaN, Number.NaN, this.region());
        } else {
          this.selectWholeAreaForCurrentRegion();
        }

        this.zoomPointControl.setValue(this.centerCity, { emitEvent: false });

        if (!this.isProductDeactive() && !this.dtgs()) {
          this.selectToday();
          this.autoRefreshOn();
        } else if (this.isProductDeactive()) {
          this.sliderArchiveOptions.set(sliderBaseOptions);
          this.isArchiveMode.set(true);
          this.setArchiveDtgs();
          this.autoRefreshOff();
        }
      } else if (this.centerCity) {
        let zoom = this.centerCity.searchName;
        if (this.centerCity.hasOwnProperty('latitude')) {
          zoom +=
            '_' +
            this.centerCity['latitude'] +
            '_' +
            this.centerCity['longitude'];
        }
        this.router.navigate(['/radar', this.region(), zoom]);
      }
    });
  }

  public onRegionSelectionChange(region: string): void {
    const safeRegion = this.getAllowedRegionOrFallback(region);

    if (safeRegion !== region) {
      this.redirectToFallbackRegion(safeRegion);
      return;
    }

    this.onPause();
    this.region.set(safeRegion);
    this.settings.update(settings => {
      return { ...settings, region: safeRegion };
    });
    this.saveUserSettings();

    this.regionControl.setValue(safeRegion, { emitEvent: false });

    this.setZoomPointsForRegion();
    this.selectWholeAreaForCurrentRegion();
    this.navigateToCity(this.centerCity);

    if (this.isArchiveMode() || this.isProductDeactive()) {
      this.setArchiveDtgs();
    } else {
      this.selectToday();
    }
  }

  private getAllowedRegionOrFallback(region?: string | null): string {
    const allowedRegions = this.regions();

    if (allowedRegions.length === 0) {
      return 'de';
    }

    if (region && allowedRegions.includes(region)) {
      return region;
    }

    return allowedRegions[0];
  }

  private redirectToFallbackRegion(region: string): void {
    this.region.set(region);
    this.regionControl.setValue(region, { emitEvent: false });

    this.settings.update(settings => {
      return { ...settings, region };
    });
    this.saveUserSettings();

    this.setZoomPointsForRegion();
    this.selectWholeAreaForCurrentRegion();

    this.router.navigate(['/radar', region, this.centerCity.searchName]);
  }

  public onCitySelectionChange(city: any): void {
    const wasPause = this.isPause();
    this.onPause();

    this.centerCity = city;
    this.zoomPointControl.setValue(city, { emitEvent: false });
    this.radarService.updateSelectedCity(city);
    this.navigateToCity(city);

    if (!wasPause) {
      this.onPlay();
    }
  }

  private navigateToCity(city: any): void {
    let zoom = city.searchName;
    if (city?.hasOwnProperty('latitude')) {
      zoom += '_' + city['latitude'] + '_' + city['longitude'];
    }
    this.router.navigate(['/radar', this.region(), zoom]);
  }

  setCenterCity(name: string, lon: number, lat: number, country: string): void {
    // Prefer the object instance from the current list (so mat-select compares consistently)
    const fromList = this.centerCitiesOfRegion().find(
      r => r.searchName === name
    );
    if (fromList) {
      this.centerCity = fromList;
      this.zoomPointControl.setValue(this.centerCity, { emitEvent: false });
      return;
    }

    // Fallback: construct a city; if coords are missing/invalid, use region center
    const fallbackCenter = this.getRegionCenter(this.region());
    const safeLat = Number.isFinite(lat) ? lat : fallbackCenter.latitude;
    const safeLon = Number.isFinite(lon) ? lon : fallbackCenter.longitude;

    this.centerCity = {
      searchName: name,
      latitude: safeLat,
      longitude: safeLon,
      country: this.region(),
    } as any;

    this.zoomPointControl.setValue(this.centerCity, { emitEvent: false });
  }

  ngOnDestroy(): void {
    this.onPause();
    this.autoRefreshOff();
  }

  // TODO: Read from User Profile
  readUserSettings(): void {
    let savedSettings;
    const savedValue = localStorage.getItem('radarSettings');
    if (savedValue) {
      savedSettings = JSON.parse(savedValue);
    }
    if (!savedValue) {
      const userProfile = this.auth.getUserProfile();
      if (userProfile['metadata'].hasOwnProperty('radar')) {
        savedSettings = userProfile['metadata']['radar'];
      }
    }
    if (savedSettings) {
      for (const key of Object.keys(savedSettings)) {
        this.settings.update(settings => {
          settings[key] = savedSettings[key];
          return { ...settings };
        });
      }
    }
  }

  // TODO: Save to User Profile
  saveUserSettings(): void {
    localStorage.setItem('radarSettings', JSON.stringify(this.settings()));
    this.auth.updateUserData('radar', this.settings());
  }

  autoRefreshOn(): void {
    if (!isNil(this.refreshSubscription)) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = null;
    }
    this.refreshSubscription = timer(120000, 120000).subscribe(() => {
      this.selectToday();
    });
  }

  autoRefreshOff(): void {
    if (!isNil(this.refreshSubscription)) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = null;
    }
  }

  getRegions(): Array<string> {
    const customerDocument = this.auth.getCustomerDocument();
    const regions = [];
    for (const product of customerDocument.radarProducts) {
      if (this.auth.isProductActive('radarProducts', product.id)) {
        const region = product.region ? product.region : 'de';
        if (regions.indexOf(region) < 0) {
          regions.push(region);
        }
      }
    }
    const sortedRegions = regions.sort((a, b) => (a < b ? 1 : -1));
    this.radarService.regions = sortedRegions;
    return sortedRegions;
  }

  private getRegionCenter(region: string): {
    longitude: number;
    latitude: number;
  } {
    const regionCenter: Record<
      string,
      { longitude: number; latitude: number }
    > = {
      de: { longitude: 9.8, latitude: 51.5 },
      ch: { longitude: 8.4, latitude: 46.8 },
      benelux: { longitude: 5.09, latitude: 52.505 },
      eu: { longitude: 5.09, latitude: 52.505 },
      es: { longitude: -3.5, latitude: 40 },
      fr: { longitude: 3.7973552, latitude: 45.8221641 },
    };
    return regionCenter[region] ?? regionCenter['de'];
  }

  private selectWholeAreaForCurrentRegion(): void {
    const wholeArea = this.centerCitiesOfRegion().find(
      center => center?.searchName === 'whole Area'
    );

    const fallbackCenter = this.getRegionCenter(this.region());
    this.centerCity =
      wholeArea ??
      ({
        searchName: 'whole Area',
        country: this.region(),
        longitude: fallbackCenter.longitude,
        latitude: fallbackCenter.latitude,
      } as any);

    this.zoomPointControl.setValue(this.centerCity, { emitEvent: false });
    this.radarService.updateSelectedCity(this.centerCity);
  }

  initZoomPoints(): void {
    this.centerCitiesAll = this.radarService.getCenterCities(this.regions());
    this.setZoomPointsForRegion();
  }

  setZoomPointsForRegion(): void {
    const region = this.region();
    const fallbackCenter = this.getRegionCenter(region);

    // Build list without mutating shared objects in centerCitiesAll
    const citiesForRegion = (this.centerCitiesAll ?? [])
      .filter((zoomPoint: any) => zoomPoint?.country === region)
      .map((zoomPoint: any) => {
        const hasLon = Object.prototype.hasOwnProperty.call(
          zoomPoint,
          'longitude'
        );
        const hasLat = Object.prototype.hasOwnProperty.call(
          zoomPoint,
          'latitude'
        );

        return {
          ...zoomPoint,
          longitude: hasLon ? zoomPoint.longitude : fallbackCenter.longitude,
          latitude: hasLat ? zoomPoint.latitude : fallbackCenter.latitude,
        };
      });

    // Ensure "whole Area" exists and is first (stable default)
    const hasWholeArea = citiesForRegion.some(
      c => c?.searchName === 'whole Area'
    );
    const wholeArea = {
      searchName: 'whole Area',
      longitude: fallbackCenter.longitude,
      latitude: fallbackCenter.latitude,
      country: region,
    };

    const finalList = hasWholeArea
      ? citiesForRegion
      : [wholeArea, ...citiesForRegion];

    this.centerCitiesOfRegion.set([...finalList]);
    // Important: do NOT set centerCity / control value here.
    // Selection is handled explicitly by callers to avoid races.
  }
  private getValidDatesForRegion() {
    const dates: Array<number> = [];
    const today: Moment = moment(moment().format('YYYY-MM-DD 00:00:00'));
    const customerDocument = this.auth.getCustomerDocument();
    if (customerDocument.hasOwnProperty('radarProducts')) {
      // all radar products
      for (const product of customerDocument['radarProducts']) {
        // was it ever active?
        if (this.auth.isProductActive('radarProducts', product.id, true)) {
          // does it contain current station?
          if (this.isRegionEqual(product['region'])) {
            const startDate = moment(
              moment(product['contractStartDtg']).format('YYYY-MM-DD 00:00:00')
            );
            let endDate = today;
            if (product['contractExpiryDtg']) {
              endDate = moment(
                moment(product['contractExpiryDtg']).format(
                  'YYYY-MM-DD 00:00:00'
                )
              );
              if (endDate.isAfter(today)) {
                endDate = today;
              }
            }
            endDate.add(12, 'hours');
            // check each day between start and expiry date
            for (
              const dtg = startDate;
              dtg.isBefore(endDate);
              dtg.add(1, 'day')
            ) {
              const timeStamp = dtg.unix();
              if (dates.indexOf(timeStamp) < 0) {
                // check season
                if (this.auth.isDateInSeason(dtg, product['saison'])) {
                  dates.push(timeStamp);
                }
              }
            }
          }
        }
      }
    }
    return dates;
  }

  private initSeasonDate() {
    if (this.isArchiveMode() || this.isProductDeactive()) {
      const dates = this.getValidDatesForRegion();
      const lastDateUnix = moment.unix(Math.max(...dates));

      this.dateControl.setValue(lastDateUnix.clone());
      this.allowedDates.set([...dates]);

      this.dateControl.updateValueAndValidity();

      this.seasonDate.set(lastDateUnix.clone());
    } else {
      this.seasonDate.set(moment().set({ hour: 0, minute: 0, second: 0 }));
    }
  }

  isRegionEqual(regionToCheck: string): boolean {
    if (!regionToCheck) {
      regionToCheck = 'de';
    }
    return regionToCheck === this.region();
  }

  selectToday(): void {
    if (this.loadingImages()) {
      return;
    }

    // Slider/dtgs are reset in applyLatestRadarImages() once fresh data has
    // actually been fetched and accepted, so a pending forecast run being
    // incomplete doesn't wipe the images currently on screen.
    this.radarService.isArchive = false;
    this.initSeasonDate();
    this.latestRadarImageInformation();
  }

  latestRadarImageInformation(): void {
    this.loadingImages.set(true);
    this.serverError.set(false);

    this.radarService.getLatestObsRun(obsRunTypes[this.region()]).subscribe(
      observations => {
        if (!this.settings().showRadarFcst) {
          this.applyLatestRadarImages(observations, null);
          return;
        }

        this.radarService
          .getLatestFcstRun(fcstRunTypes[this.region()])
          .subscribe(
            fcstData => {
              const currentFcstRun = fcstData[1];
              this.applyLatestRadarImages(observations, currentFcstRun);
            },
            fcstErr => {
              this.serverError.set(true);
              this.loadingImages.set(false);
            }
          );
      },
      err => {
        //console.error('latestRadarImageInformation', err);
        this.serverError.set(true);
        this.loadingImages.set(false);
      }
    );
  }

  private applyLatestRadarImages(
    observations: { dtg: string; item: string }[],
    currentFcstRun: { runtime: string; validAt: { dtg: string }[] } | null
  ): void {
    this.restartSlider();

    const zeroObsDtg = observations[0].dtg;
    const firstObsDtg = observations[1].dtg;
    this.interval.set(moment(zeroObsDtg).diff(firstObsDtg, 'minutes'));

    this.calculateDtgs(observations, currentFcstRun);
  }

  calculateDtgs(
    observations: { dtg: string; item: string }[],
    currentFcstRun: { runtime: string; validAt: { dtg: string }[] } | null
  ): void {
    const lastObservationUtc = observations[0].dtg;
    const limit = moment(lastObservationUtc).subtract(
      this.settings().timerange,
      'minutes'
    );

    const obsTimestamps = new Set<number>();

    for (let i = 0; i < observations.length; i++) {
      const dtg = moment(observations[i].dtg);

      if (dtg.isBefore(limit)) {
        continue;
      }

      if (dtg.isAfter(lastObservationUtc)) {
        break;
      }

      const dtgUtc = dtg.clone().utc();
      const timestamp = dtg.clone().unix();
      obsTimestamps.add(timestamp);
      this.dtgs.update(dtgs => {
        return [
          {
            mapServer: dtgUtc.format('YYYYMMDDHHmm'),
            slider: timestamp,
            info: dtg.clone().format('dd DD.MM.YYYY HH:mm'),
            visible: i === 0,
            isFcst: false,
            isArchive: false,
            issue: 0,
          },
          ...dtgs,
        ];
      });
    }

    if (this.settings().showRadarFcst && currentFcstRun) {
      const fcstLimit = moment(lastObservationUtc).add(
        this.settings().timerange,
        'minutes'
      );

      const fcstDtgs = [...currentFcstRun.validAt].sort(
        (a, b) => moment(a.dtg).valueOf() - moment(b.dtg).valueOf()
      );

      const runTime = moment(currentFcstRun.runtime);
      const formatedRunTime = runTime.clone().utc().format('YYYYMMDDHHmm');

      for (let i = 0; i < fcstDtgs.length; i++) {
        const dtg = moment(fcstDtgs[i].dtg);
        const timestamp = dtg.clone().unix();

        // Obs wins for every timestep both runs share - the fcst run can
        // overlap with more than just the latest obs step (e.g. obs ends at
        // 11:15 but the fcst run already starts at 11:10), so skip any fcst
        // step whose valid time matches an obs step already added above,
        // instead of only comparing against the fcst run's own issue time.
        if (obsTimestamps.has(timestamp)) {
          continue;
        }

        if (dtg.isAfter(fcstLimit)) {
          break;
        }

        this.dtgs.update(dtgs => {
          return [
            ...dtgs,
            {
              mapServer: formatedRunTime,
              slider: timestamp,
              info: dtg.clone().format('dd DD.MM.YYYY HH:mm'),
              visible: false,
              isFcst: true,
              isArchive: false,
              issue: i,
            },
          ];
        });
      }
    }

    this.setSliderOptions();
    this.loadingImages.set(false);
  }

  setSliderOptions = () => {
    const fcstIndex = this.dtgs().findIndex(dtg => dtg.isFcst);
    if (fcstIndex === -1) {
      this.sliderDate.set(this.dtgs()[this.dtgs().length - 1].slider);
    } else {
      this.sliderDate.set(this.dtgs()[fcstIndex - 1].slider);
    }

    this.dtgIndex.set(
      this.dtgs().findIndex(dtg => this.sliderDate() === dtg.slider)
    );

    const unixDates = this.dtgs().map(dtg => dtg.slider);

    this.sliderOptions.set({
      showTicks: true,
      showTicksValues: false,
      hidePointerLabels: true,
      hideLimitLabels: true,
      ticksTooltip: (i: number): string => {
        return moment.unix(unixDates[i]).format('HH:mm');
      },
      stepsArray: unixDates.map((step: number, index: number) => {
        const stepDtg = moment.unix(step);
        const isNow =
          step === this.sliderDate() && this.settings().showRadarFcst;

        return {
          value: step,
          legend: isNow
            ? this.translate.instant('PRODUCT.RADAR.NOW')
            : this.shouldShowSliderTimeLabel(index, unixDates.length)
              ? stepDtg.format('HH:mm')
              : '',
        };
      }),
      getTickColor: (value: number): string => {
        if (this.dtgs().length > value && this.dtgs()[value].isFcst) {
          return 'red';
        }

        return '#399DE6';
      },
      translate: (value: number, label: LabelType): string => {
        const stepDtg = moment.unix(value);

        return label !== LabelType.TickValue ? stepDtg.format('HH:mm') : '';
      },
    });
  };

  setArchiveDtgs(radarStartTime?: moment.Moment): void {
    this.initSeasonDate();
    this.restartSlider();

    let currentRadarStartTime = radarStartTime;
    if (isNil(radarStartTime)) {
      currentRadarStartTime = this.seasonDate().clone();
    }

    currentRadarStartTime = currentRadarStartTime
      .clone()
      .set('minutes', 0)
      .set('hours', 0)
      .set('milliseconds', 0)
      .set('seconds', 0);

    const now = moment();
    const isToday = currentRadarStartTime.isSame(now, 'day');

    const currentRadarEndDate = isToday
      ? now.clone()
      : currentRadarStartTime.clone().add(1, 'days');

    this.radarService
      .getObsRunBetweenDates(
        obsRunTypes[this.region()],
        currentRadarStartTime,
        currentRadarEndDate
      )
      .subscribe(data => {
        const zeroObsDtg = data[0].dtg;
        const firstObsDtg = data[1].dtg;

        this.interval.set(moment(firstObsDtg).diff(zeroObsDtg, 'minutes'));

        for (let i = 0; i < data.length; i++) {
          const dtg = moment(data[i].dtg);

          if (!dtg.isSame(currentRadarStartTime, 'day')) {
            continue;
          }
          const dtgUtc = dtg.clone().utc();
          const timestamp = dtg.clone().unix();
          this.dtgs.update(dtgs => {
            return [
              ...dtgs,
              {
                mapServer: dtgUtc.format('YYYYMMDDHHmm'),
                slider: timestamp,
                info: dtg.clone().format('dd DD.MM.YYYY HH:mm'),
                visible: false,
                isFcst: false,
                isArchive: true,
                issue: 0,
              },
            ];
          });
        }

        this.setSliderArchiveOptions();
      });
  }

  setSliderArchiveOptions() {
    this.sliderDate.set(this.dtgs()[this.dtgs().length - 1].slider);
    this.setRadarTime(this.sliderDate());

    const unixDates = this.dtgs().map(dtg => dtg.slider);

    this.sliderArchiveOptions.set({
      showTicks: true,
      showTicksValues: false,
      hidePointerLabels: true,
      hideLimitLabels: true,
      ticksTooltip: (i: number): string => {
        return moment.unix(unixDates[i]).format('HH:mm');
      },
      stepsArray: unixDates.map((step: number, index: number) => {
        const stepDtg = moment.unix(step);

        return {
          value: step,
          legend: this.shouldShowSliderTimeLabel(index, unixDates.length)
            ? stepDtg.format('HH:mm')
            : '',
        };
      }),
      getTickColor: (): string => {
        return 'grey';
      },
      translate: (value: number, label: LabelType): string => {
        const stepDtg = moment.unix(value);

        return label !== LabelType.TickValue ? stepDtg.format('HH:mm') : '';
      },
    });
  }

  private shouldShowSliderTimeLabel(
    index: number,
    totalTicks: number
  ): boolean {
    const maxVisibleLabels = 20;

    if (totalTicks <= maxVisibleLabels) {
      return true;
    }

    if (index === 0 || index === totalTicks - 1) {
      return true;
    }

    const labelInterval = Math.ceil(totalTicks / maxVisibleLabels);

    return index % labelInterval === 0;
  }

  setRadarTime(timestamp: number): void {
    const dtgs = this.dtgs();

    if (!dtgs?.length) {
      return;
    }

    const index = dtgs.findIndex(dtg => dtg.slider === timestamp);

    if (index < 0) {
      return;
    }

    this.setImageOpacity(index);
    this.dtgIndex.set(index);
  }

  setImageOpacity(index: number): void {
    const dtgs = this.dtgs();

    if (!dtgs?.length || index < 0 || index >= dtgs.length) {
      return;
    }

    this.dtgs.set(
      dtgs.map((dtg, i) => ({
        ...dtg,
        visible: i === index,
      }))
    );
  }

  startTimer(): void {
    this.stopTimer();

    const radarTimer = timer(
      0,
      this.animationSpeedMapping[this.settings().animationSpeed]
    );

    this.animationSubscription = radarTimer.subscribe(() => {
      const dtgs = this.dtgs();

      if (!dtgs?.length) {
        return;
      }

      const nextIndex =
        this.dtgIndex() + 1 >= dtgs.length ? 0 : this.dtgIndex() + 1;

      this.dtgIndex.set(nextIndex);
      this.setImageOpacity(nextIndex);
      this.sliderDate.set(dtgs[nextIndex].slider);
    });
  }

  stopTimer(): void {
    if (!isNil(this.animationSubscription)) {
      this.animationSubscription.unsubscribe();
      this.animationSubscription = null;
    }
  }

  onPlay(): void {
    this.isPause.set(false);
    this.startTimer();
  }

  onPause(): void {
    this.isPause.set(true);
    this.stopTimer();
  }

  onSliderChange(event: ChangeContext): void {
    this.setRadarTime(event.value);
    this.onPause();
  }

  onDateChange(event: MatDatepickerInputEvent<Moment>): void {
    this.onPause();

    this.sliderArchiveOptions.set(sliderBaseOptions);

    this.setArchiveDtgs(event.value.clone());

    this.seasonDate.set(event.value.clone());
    this.dateControl.setValue(this.seasonDate().clone());
  }

  onSettingsChange(
    event: ChangeContext,
    property:
      | 'intervalCurrent'
      | 'intervalArchive'
      | 'timerange'
      | 'animationSpeed'
  ) {
    this.settings.update(settings => {
      return { ...settings, [property]: event.value };
    });
  }

  private restartSlider() {
    this.dtgs.set([]);
    this.dtgIndex.set(0);
  }

  onSetShowArchive(event: MatSlideToggleChange): void {
    const isArchiveMode = event.checked;
    this.onPause();
    if (isArchiveMode) {
      this.sliderArchiveOptions.set(sliderBaseOptions);
      this.isArchiveMode.set(true);
      this.setArchiveDtgs();
      this.autoRefreshOff();
    } else {
      this.sliderOptions.set(sliderBaseOptions);
      this.dtgIndex.set(0);
      this.isArchiveMode.set(false);
      this.selectToday();
      this.autoRefreshOn();
    }
  }

  // Mobile archive button: a single tap toggles archive mode directly
  // instead of opening a panel to flip the desktop slide-toggle.
  onToggleArchiveMobile(): void {
    this.onSetShowArchive({
      checked: !this.isArchiveMode(),
    } as MatSlideToggleChange);
  }

  onCellTrackingChange(event: MatSlideToggleChange): void {
    this.onPause();
    this.settings.update(settings => {
      return { ...settings, showCellTracking: event.checked };
    });
    this.saveUserSettings();
    this.selectToday();
  }

  onRadarFcstChange(event: MatSlideToggleChange): void {
    this.onPause();
    this.settings.update(settings => {
      return { ...settings, showRadarFcst: event.checked };
    });
    this.saveUserSettings();
    this.selectToday();
  }

  onSpeedSliderChange(event: ChangeContext): void {
    this.onPause();
    this.settings.update(settings => {
      return { ...settings, animationSpeed: event.value };
    });
    this.saveUserSettings();
    if (!this.isPause()) {
      this.onPlay();
    }
  }

  onTimeRangeChange(event: ChangeContext): void {
    this.onPause();
    this.settings.update(settings => {
      return { ...settings, timerange: event.value };
    });
    this.sliderOptions.set(sliderBaseOptions);
    this.selectToday();
    this.saveUserSettings();
  }

  onResetSettings(): void {
    this.onPause();
    this.settings.set({
      timerange: 120,
      animationSpeed: 4,
      showCellTracking: true,
      showRadarFcst: true,
      region: null,
    });
    this.saveUserSettings();

    if (this.isArchiveMode()) {
      this.sliderArchiveOptions.set(sliderBaseOptions);
      this.setArchiveDtgs();
    } else {
      this.sliderOptions.set(sliderBaseOptions);
      this.selectToday();
    }
  }

  onMapLoad(map: mapboxgl.Map) {
    this.map = map;
    this.setConcentricCirclesControl();
    setPigeonsRoute(map, this.auth.isProductActive('pigeonProducts'));
  }

  private setConcentricCirclesControl() {
    const binaryButtonEl: NgElement & WithProperties<BinaryButtonComponent> =
      document.createElement('binary-button-element') as any;
    binaryButtonEl.iconProperties = {
      iconMappings: {
        ban: {
          mainIcon: 'ban',
          mainIcon_transform: 'grow-8',
        },
        sort: {
          mainIcon: 'circle',
          mainIcon_transform: 'grow-8',
        },
      },
      icons: [
        { iconName: 'ban', id: '1' },
        {
          iconName: 'circle',
          id: '0',
        },
      ],
    } as any;
    binaryButtonEl.selectedbuttonId$ = this.concentricCircles$ as any;
    this.map.addControl(new CustomComponentControl(binaryButtonEl));
  }

  private listenConcentricCirclesButtonSelectionChange$() {
    this.concentricCircles$
      .pipe(
        filter(id => !isNil(id)),
        tap(id => {
          switch (id) {
            case BinaryButton.disabled: {
              localStorage.setItem(
                concentricCircleControlEnabled,
                BinaryButton.disabled
              );
              this.mapComponent?.removeCircles();
              break;
            }
            case BinaryButton.enabled: {
              localStorage.setItem(
                concentricCircleControlEnabled,
                BinaryButton.enabled
              );
              this.mapComponent?.paintCircles();
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(noop);
  }

  compareZoomPoints = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;

    // "whole Area" should match by region/country + name
    const aName = a.searchName ?? a['searchName'];
    const bName = b.searchName ?? b['searchName'];

    const aCountry = a.country ?? a['country'];
    const bCountry = b.country ?? b['country'];

    if (aName === 'whole Area' || bName === 'whole Area') {
      return aName === bName && aCountry === bCountry;
    }

    // Cities: match by name + coords (covers duplicates by name across regions)
    const aLat = a.latitude ?? a['latitude'];
    const bLat = b.latitude ?? b['latitude'];
    const aLon = a.longitude ?? a['longitude'];
    const bLon = b.longitude ?? b['longitude'];

    return (
      aName === bName && aLat === bLat && aLon === bLon && aCountry === bCountry
    );
  };
}
